import React, { useMemo, useState, Component } from 'react';
import { SearchIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, FilterIcon, XIcon, ExternalLinkIcon, CpuIcon, MemoryStickIcon, GaugeIcon, LeafIcon } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────────
interface Model {
  name: string;
  id: string;
  description: string;
  provider: string;
  contextLength: string;
  params: string;
  paramsSortValue: number;
  inputPrice: string;
  outputPrice: string;
  status: 'available' | 'coming-soon';
  hardware: string;
  useCases: string[];
  tags?: string[];
}
type SortOption = 'newest' | 'name-az' | 'largest';
const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeA3akOwONwkYJLK4nX5RaNw1wlVWWLd6dPKvH2FSMPjvA9IA/viewform';
// ─── Data ────────────────────────────────────────────────────────
const models: Model[] = [
// ── Available ──
{
  name: 'Llama 3.3 70B Instruct',
  id: 'meta-llama/llama-3.3-70b-instruct',
  description: "Meta's advanced 70B instruction-tuned model delivering Llama 3.1 405B-level performance. Optimized for FPGA inference with sub-millisecond latency.",
  provider: 'meta-llama',
  contextLength: '128K context',
  params: '70B parameters',
  paramsSortValue: 70,
  inputPrice: '$0.59/M input tokens',
  outputPrice: '$0.61/M output tokens',
  status: 'available',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual'],
  tags: ['Programming (#1)', 'Technology (#3)']
},
// ── Coming Soon ──
{
  name: 'Kimi K2.5',
  id: 'moonshotai/kimi-k2.5',
  description: "Moonshot AI's latest flagship model with strong reasoning and multilingual capabilities. Targeting FPGA synthesis for cost-efficient high-performance inference.",
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '1T MoE parameters',
  paramsSortValue: 1000,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual']
}, {
  name: 'Kimi K2 0905',
  id: 'moonshotai/kimi-k2-0905',
  description: 'Updated Kimi K2 release with improved instruction following and coding performance. FPGA optimization planned for low-latency deployment.',
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '1T MoE parameters',
  paramsSortValue: 1000,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'Kimi K2 Thinking',
  id: 'moonshotai/kimi-k2-thinking',
  description: 'Chain-of-thought reasoning variant of Kimi K2, excelling at complex multi-step problems. FPGA synthesis targeting efficient long-context reasoning.',
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '1T MoE parameters',
  paramsSortValue: 1000,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Reasoning']
}, {
  name: 'Kimi K2 0711',
  id: 'moonshotai/kimi-k2',
  description: 'Original Kimi K2 release with balanced performance across text generation, coding, and reasoning tasks. Planned for FPGA-accelerated inference.',
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '1T MoE parameters',
  paramsSortValue: 1000,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'Kimi K2 0905 (exacto)',
  id: 'moonshotai/kimi-k2-0905:exacto',
  description: 'Precision-tuned variant of Kimi K2 0905 optimized for accuracy-critical tasks. Ideal candidate for deterministic FPGA inference pipelines.',
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '1T MoE parameters',
  paramsSortValue: 1000,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'Kimi Dev 72B',
  id: 'moonshotai/kimi-dev-72b',
  description: "Moonshot's 72B developer-focused model with strong coding and tool-use capabilities. FPGA synthesis in progress for high-throughput code generation.",
  provider: 'moonshotai',
  contextLength: '128K context',
  params: '72B parameters',
  paramsSortValue: 72,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'Kimi VL A3B Thinking',
  id: 'moonshotai/kimi-vl-a3b-thinking',
  description: 'Compact vision-language model with chain-of-thought reasoning over images and text. Efficient architecture well-suited for FPGA deployment.',
  provider: 'moonshotai',
  contextLength: '64K context',
  params: '3B parameters',
  paramsSortValue: 3,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'Intel/Altera Agilex 7',
  useCases: ['Text Generation', 'Reasoning', 'Vision']
}, {
  name: 'Llama 4 Maverick',
  id: 'meta-llama/llama-4-maverick',
  description: "Meta's Llama 4 Maverick with mixture-of-experts architecture delivering frontier performance. FPGA synthesis planned for efficient sparse computation.",
  provider: 'meta-llama',
  contextLength: '1M context',
  params: '400B MoE parameters',
  paramsSortValue: 400,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual']
}, {
  name: 'Llama 4 Scout',
  id: 'meta-llama/llama-4-scout',
  description: "Meta's Llama 4 Scout optimized for efficiency with strong performance across benchmarks. Excellent candidate for FPGA deployment at scale.",
  provider: 'meta-llama',
  contextLength: '512K context',
  params: '109B MoE parameters',
  paramsSortValue: 109,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual']
}, {
  name: 'Llama 3.1 70B Instruct',
  id: 'meta-llama/llama-3.1-70b-instruct',
  description: "Meta's 70B instruction-tuned model with strong multilingual and tool-use capabilities. FPGA optimization targeting competitive latency and throughput.",
  provider: 'meta-llama',
  contextLength: '128K context',
  params: '70B parameters',
  paramsSortValue: 70,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual']
}, {
  name: 'Llama 3.1 8B Instruct',
  id: 'meta-llama/llama-3.1-8b-instruct',
  description: 'Lightweight yet capable 8B model ideal for low-latency applications. FPGA synthesis delivers exceptional throughput at minimal power consumption.',
  provider: 'meta-llama',
  contextLength: '128K context',
  params: '8B parameters',
  paramsSortValue: 8,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code', 'Multilingual']
}, {
  name: 'Llama 3.2 3B Instruct',
  id: 'meta-llama/llama-3.2-3b-instruct',
  description: 'Ultra-compact 3B model designed for edge deployment. Perfect fit for FPGA inference with minimal resource requirements and fast response times.',
  provider: 'meta-llama',
  contextLength: '128K context',
  params: '3B parameters',
  paramsSortValue: 3,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'Intel/Altera Agilex 7',
  useCases: ['Text Generation', 'Code', 'Multilingual']
}, {
  name: 'Llama 3 8B Instruct',
  id: 'meta-llama/llama-3-8b-instruct',
  description: 'Original Llama 3 8B with solid instruction-following performance. Efficient architecture well-suited for FPGA-accelerated inference.',
  provider: 'meta-llama',
  contextLength: '8K context',
  params: '8B parameters',
  paramsSortValue: 8,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Code']
}, {
  name: 'Llama 3 8B Lunaris',
  id: 'sao10k/l3-lunaris-8b',
  description: 'Community fine-tune of Llama 3 8B with enhanced creative writing and roleplay capabilities. Targeting FPGA deployment for low-latency generation.',
  provider: 'sao10k',
  contextLength: '8K context',
  params: '8B parameters',
  paramsSortValue: 8,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation']
}, {
  name: 'Llama 3.1 Nemotron Ultra 253B',
  id: 'nvidia/llama-3.1-nemotron-ultra-253b-v1',
  description: "NVIDIA's Nemotron Ultra built on Llama 3.1 with 253B parameters. Exceptional reasoning and instruction following, targeting FPGA synthesis for cost-efficient deployment.",
  provider: 'nvidia',
  contextLength: '128K context',
  params: '253B parameters',
  paramsSortValue: 253,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'MiniMax M2.5',
  id: 'minimax/minimax-m2.5',
  description: "MiniMax's latest model with strong performance across text generation and reasoning benchmarks. FPGA optimization planned for high-throughput inference.",
  provider: 'minimax',
  contextLength: '1M context',
  params: '456B MoE parameters',
  paramsSortValue: 456,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning', 'Multilingual']
}, {
  name: 'Mistral 7B Instruct v0.3',
  id: 'mistralai/mistral-7b-instruct',
  description: 'Efficient 7B model with strong instruction-following capabilities. FPGA optimization in progress for ultra-low-latency deployment.',
  provider: 'mistralai',
  contextLength: '32K context',
  params: '7B parameters',
  paramsSortValue: 7,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'Intel/Altera Agilex 7',
  useCases: ['Text Generation', 'Code']
}, {
  name: 'Qwen 2.5 72B Instruct',
  id: 'qwen/qwen-2.5-72b-instruct',
  description: "Alibaba's flagship 72B model with strong multilingual and reasoning capabilities. FPGA synthesis targeting competitive price-performance.",
  provider: 'qwen',
  contextLength: '128K context',
  params: '72B parameters',
  paramsSortValue: 72,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Alveo U250',
  useCases: ['Text Generation', 'Reasoning', 'Multilingual']
}, {
  name: 'Phi-3 Mini 3.8B',
  id: 'microsoft/phi-3-mini',
  description: "Microsoft's compact yet powerful 3.8B model. Perfect candidate for FPGA deployment with minimal resource requirements and impressive performance.",
  provider: 'microsoft',
  contextLength: '128K context',
  params: '3.8B parameters',
  paramsSortValue: 3.8,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'Intel/Altera Agilex 7',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}, {
  name: 'DeepSeek V3',
  id: 'deepseek/deepseek-v3',
  description: "DeepSeek's latest MoE architecture model with exceptional reasoning and coding performance. FPGA synthesis planned for efficient sparse computation.",
  provider: 'deepseek',
  contextLength: '128K context',
  params: '671B MoE parameters',
  paramsSortValue: 671,
  inputPrice: 'TBD',
  outputPrice: 'TBD',
  status: 'coming-soon',
  hardware: 'AMD/Xilinx Versal',
  useCases: ['Text Generation', 'Code', 'Reasoning']
}];
// ─── Filter Section Component ────────────────────────────────────
interface FilterSectionProps {
  title: string;
  options: string[];
  selected: string[];
  onToggle: (option: string) => void;
}
function FilterSection({
  title,
  options,
  selected,
  onToggle
}: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="border-b border-gate-border/50 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0" data-id="element-501">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full text-sm font-semibold text-gate-text-primary mb-2 hover:text-white transition-colors" data-id="element-502">
        <span data-id="element-503">{title}</span>
        {isOpen ? <ChevronDownIcon className="w-4 h-4 text-gate-text-muted" data-id="element-504" /> : <ChevronRightIcon className="w-4 h-4 text-gate-text-muted" data-id="element-505" />}
      </button>
      {isOpen && <div className="space-y-1.5 pl-0.5" data-id="element-506">
          {options.map(option => {
        const isSelected = selected.includes(option);
        return <label key={option} className="flex items-center gap-2.5 cursor-pointer group" data-id="element-507">
                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'bg-gate-teal border-gate-teal' : 'border-gate-border group-hover:border-gate-text-muted'}`} data-id="element-508">
                  {isSelected && <CheckIcon className="w-3 h-3 text-gate-dark" data-id="element-509" />}
                </div>
                <input type="checkbox" checked={isSelected} onChange={() => onToggle(option)} className="sr-only" data-id="element-510" />
                <span className="text-sm text-gate-text-secondary group-hover:text-white transition-colors" data-id="element-511">
                  {option}
                </span>
              </label>;
      })}
        </div>}
    </div>;
}
// ─── Copy Button Component ───────────────────────────────────────
function CopyIdButton({
  modelId
}: {
  modelId: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(modelId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <button onClick={handleCopy} className="p-1 rounded text-gate-text-muted hover:text-gate-teal transition-colors" aria-label={`Copy model ID: ${modelId}`} title={copied ? 'Copied!' : 'Copy model ID'} data-id="element-512">
      {copied ? <CheckIcon className="w-4 h-4 text-gate-teal" data-id="element-513" /> : <CopyIcon className="w-4 h-4" data-id="element-514" />}
    </button>;
}
// ─── Model Card Component ────────────────────────────────────────
function ModelCard({
  model
}: {
  model: Model;
}) {
  const isComingSoon = model.status === 'coming-soon';
  return <div className={`py-6 border-b border-gate-border/40 ${isComingSoon ? 'opacity-70' : ''}`} data-id="element-515">
      {/* Row 1: Name + copy + params */}
      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-516">
        <div className="flex items-center gap-2 min-w-0" data-id="element-517">
          <h3 className="text-lg font-bold text-white truncate" data-id="element-518">
            {model.name}
          </h3>
          <CopyIdButton modelId={model.id} data-id="element-519" />
          {isComingSoon && <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-gate-amber/10 text-gate-amber border border-gate-amber/20" data-id="element-520">
              Coming Soon
            </span>}
          {model.tags?.map(tag => {
          const colors: Record<string, string> = {
            'Programming (#1)': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
            'Technology (#3)': 'bg-green-500/10 text-green-400 border-green-500/20'
          };
          return <span key={tag} className={`flex-shrink-0 hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${colors[tag] || 'bg-gate-surface text-gate-text-muted border-gate-border'}`} data-id="element-521">
                <span className="w-1.5 h-1.5 rounded-full bg-current" data-id="element-522" />
                {tag}
              </span>;
        })}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-523">
          <span className="text-sm text-gate-text-muted font-mono" data-id="element-524">
            {model.params}
          </span>
          {isComingSoon ? <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gate-teal/30 text-gate-teal hover:bg-gate-teal/10 transition-colors" data-id="element-525">
              Request
              <ExternalLinkIcon className="w-3 h-3" data-id="element-526" />
            </a> : <a href={FORM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-gate-teal text-gate-dark hover:bg-gate-teal/90 transition-colors shadow-[0_0_10px_-3px_rgba(0,212,170,0.4)]" data-id="element-527">
              Get API Key
            </a>}
        </div>
      </div>

      {/* Row 2: Description */}
      <p className="text-sm text-gate-text-secondary leading-relaxed mb-3 line-clamp-2" data-id="element-528">
        {model.description}
      </p>

      {/* Row 3: Meta info */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm" data-id="element-529">
        <span className="text-gate-text-muted" data-id="element-530">by</span>
        <span className="text-gate-teal font-medium" data-id="element-531">{model.provider}</span>
        <span className="text-gate-border" data-id="element-532">|</span>
        <span className="text-gate-text-muted" data-id="element-533">{model.contextLength}</span>
        <span className="text-gate-border" data-id="element-534">|</span>
        <span className="text-gate-text-muted" data-id="element-535">{model.inputPrice}</span>
        <span className="text-gate-border" data-id="element-536">|</span>
        <span className="text-gate-text-muted" data-id="element-537">{model.outputPrice}</span>
      </div>
    </div>;
}
// ─── Main Models Page ────────────────────────────────────────────
export function ModelsPage() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Filter state
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [hardwareFilters, setHardwareFilters] = useState<string[]>([]);
  const [useCaseFilters, setUseCaseFilters] = useState<string[]>([]);
  const [sizeFilters, setSizeFilters] = useState<string[]>([]);
  const toggleFilter = (current: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
    setter(current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
  };
  const getSizeCategory = (params: number): string => {
    if (params < 10) return '<10B';
    if (params <= 70) return '10-70B';
    return '70B+';
  };
  const getStatusLabel = (status: string): string => {
    return status === 'available' ? 'Available' : 'Coming Soon';
  };
  const getHardwareVendor = (hardware: string): string => {
    if (hardware.includes('AMD') || hardware.includes('Xilinx')) return 'AMD/Xilinx';
    if (hardware.includes('Intel') || hardware.includes('Altera')) return 'Intel/Altera';
    return hardware;
  };
  const filteredModels = useMemo(() => {
    let result = [...models];
    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(m => m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q) || m.description.toLowerCase().includes(q));
    }
    // Status filter
    if (statusFilters.length > 0) {
      result = result.filter(m => statusFilters.includes(getStatusLabel(m.status)));
    }
    // Hardware filter
    if (hardwareFilters.length > 0) {
      result = result.filter(m => hardwareFilters.includes(getHardwareVendor(m.hardware)));
    }
    // Use case filter
    if (useCaseFilters.length > 0) {
      result = result.filter(m => useCaseFilters.some(uc => m.useCases.includes(uc)));
    }
    // Size filter
    if (sizeFilters.length > 0) {
      result = result.filter(m => sizeFilters.includes(getSizeCategory(m.paramsSortValue)));
    }
    // Sort
    switch (sort) {
      case 'name-az':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'largest':
        result.sort((a, b) => b.paramsSortValue - a.paramsSortValue);
        break;
      case 'newest':
      default:
        // Keep original order (newest first)
        break;
    }
    return result;
  }, [search, sort, statusFilters, hardwareFilters, useCaseFilters, sizeFilters]);
  const activeFilterCount = statusFilters.length + hardwareFilters.length + useCaseFilters.length + sizeFilters.length;
  const filterSidebar = <div className="space-y-0" data-id="element-538">
      <FilterSection title="Status" options={['Available', 'Coming Soon']} selected={statusFilters} onToggle={v => toggleFilter(statusFilters, setStatusFilters, v)} data-id="element-539" />
      <FilterSection title="Hardware Target" options={['AMD/Xilinx', 'Intel/Altera']} selected={hardwareFilters} onToggle={v => toggleFilter(hardwareFilters, setHardwareFilters, v)} data-id="element-540" />
      <FilterSection title="Use Case" options={['Text Generation', 'Code', 'Reasoning', 'Multilingual', 'Vision']} selected={useCaseFilters} onToggle={v => toggleFilter(useCaseFilters, setUseCaseFilters, v)} data-id="element-541" />
      <FilterSection title="Model Size" options={['<10B', '10-70B', '70B+']} selected={sizeFilters} onToggle={v => toggleFilter(sizeFilters, setSizeFilters, v)} data-id="element-542" />
    </div>;
  return <div className="min-h-screen bg-gate-dark" data-id="element-543">
      {/* Mobile filter toggle */}
      <div className="lg:hidden sticky top-16 z-30 bg-gate-dark/90 backdrop-blur-sm border-b border-gate-border/50 px-4 py-2" data-id="element-544">
        <button onClick={() => setMobileFiltersOpen(true)} className="flex items-center gap-2 text-sm text-gate-text-secondary hover:text-white transition-colors" data-id="element-545">
          <FilterIcon className="w-4 h-4" data-id="element-546" />
          <span data-id="element-547">Filters</span>
          {activeFilterCount > 0 && <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-gate-teal/10 text-gate-teal" data-id="element-548">
              {activeFilterCount}
            </span>}
        </button>
      </div>

      {/* Mobile filter overlay */}
      {mobileFiltersOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-549">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} data-id="element-550" />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-gate-dark border-r border-gate-border overflow-y-auto p-6" data-id="element-551">
            <div className="flex items-center justify-between mb-6" data-id="element-552">
              <span className="text-sm font-semibold text-white" data-id="element-553">Filters</span>
              <button onClick={() => setMobileFiltersOpen(false)} className="p-1 text-gate-text-muted hover:text-white transition-colors" aria-label="Close filters" data-id="element-554">
                <XIcon className="w-5 h-5" data-id="element-555" />
              </button>
            </div>
            {filterSidebar}
          </div>
        </div>}

      <div className="max-w-[90rem] mx-auto flex" data-id="element-556">
        {/* Left Sidebar — desktop filters */}
        <aside className="hidden lg:block w-56 flex-shrink-0 border-r border-gate-border/50 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto p-6" data-id="element-557">
          {filterSidebar}
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-6 sm:px-8 lg:px-12 py-10 max-w-4xl" data-id="element-558">
          {/* Header */}
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-559">
            Models
          </h1>
          <p className="text-lg text-gate-text-secondary mb-8" data-id="element-560">
            FPGA-optimized models available through the OpenFPGA API
          </p>

          {/* Beta Release */}
          <section className="mb-10" data-id="element-561">
            <h2 className="text-xl font-bold text-white mb-3" data-id="element-562">
              Beta Release: FPGA-Optimized Inference
            </h2>
            <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-563">
              OpenFPGA is currently in{' '}
              <span className="text-gate-teal font-semibold" data-id="element-564">Beta</span> with
              FPGA-optimized inference for{' '}
              <span className="text-gate-teal font-semibold" data-id="element-565">
                Llama 3.3 70B
              </span>
              . We're actively expanding our model catalog—additional models
              will be announced as we complete FPGA synthesis and optimization.
            </p>
            <div className="rounded-lg border border-gate-teal/20 bg-gate-teal/5 p-4" data-id="element-566">
              <p className="text-sm text-gate-text-secondary" data-id="element-567">
                <span className="text-gate-teal font-medium" data-id="element-568">Beta:</span>{' '}
                Performance benchmarks and pricing may change as we continue to
                optimize our FPGA synthesis pipeline.
              </p>
            </div>
          </section>

          {/* FPGA Synthesis Process */}
          <section className="mb-10" data-id="element-569">
            <h2 className="text-xl font-bold text-white mb-3" data-id="element-570">
              FPGA Synthesis Process
            </h2>
            <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-571">
              Each model on OpenFPGA undergoes specialized synthesis to optimize
              performance on FPGA architecture. This process includes:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-id="element-572">
              {[{
              icon: CpuIcon,
              title: 'Hardware mapping',
              desc: 'Converting neural network operations into efficient FPGA logic'
            }, {
              icon: MemoryStickIcon,
              title: 'Memory optimization',
              desc: 'Minimizing bandwidth requirements through intelligent caching'
            }, {
              icon: GaugeIcon,
              title: 'Pipeline optimization',
              desc: 'Maximizing throughput while reducing latency'
            }, {
              icon: LeafIcon,
              title: 'Power efficiency tuning',
              desc: 'Achieving optimal performance-per-watt ratios'
            }].map(item => <div key={item.title} className="flex items-start gap-3 p-3 rounded-lg bg-gate-surface/40 border border-gate-border" data-id="element-573">
                  <div className="w-7 h-7 rounded-md bg-gate-teal/10 border border-gate-teal/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-574">
                    <item.icon className="w-3.5 h-3.5 text-gate-teal" data-id="element-575" />
                  </div>
                  <div data-id="element-576">
                    <span className="text-white font-semibold text-sm" data-id="element-577">
                      {item.title}
                    </span>
                    <span className="text-gate-text-secondary text-sm" data-id="element-578">
                      {' '}
                      — {item.desc}
                    </span>
                  </div>
                </div>)}
            </div>
            <p className="text-sm text-gate-text-secondary leading-relaxed" data-id="element-579">
              This synthesis ensures you get GPU-comparable performance at
              significantly lower cost and power consumption.
            </p>
          </section>

          {/* Divider */}
          <div className="border-t border-gate-border/50 mb-8" data-id="element-580" />

          {/* Search + Sort row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4" data-id="element-581">
            <div className="relative flex-1" data-id="element-582">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-583">
                <SearchIcon className="h-4 w-4 text-gate-text-muted" data-id="element-584" />
              </div>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} className="block w-full pl-10 pr-4 py-2 border border-gate-border rounded-lg bg-gate-surface text-gate-text-primary placeholder-gate-text-muted focus:outline-none focus:ring-1 focus:ring-gate-teal focus:border-gate-teal text-sm transition-all" placeholder="Search models..." data-id="element-585" />
            </div>
            <select value={sort} onChange={e => setSort(e.target.value as SortOption)} className="px-3 py-2 border border-gate-border rounded-lg bg-gate-surface text-gate-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-gate-teal focus:border-gate-teal cursor-pointer" data-id="element-586">
              <option value="newest" data-id="element-587">Newest</option>
              <option value="name-az" data-id="element-588">Name A–Z</option>
              <option value="largest" data-id="element-589">Largest</option>
            </select>
          </div>

          {/* Model count */}
          <p className="text-sm text-gate-text-muted mb-2" data-id="element-590">
            {filteredModels.length} model
            {filteredModels.length !== 1 ? 's' : ''}
          </p>

          {/* Model list */}
          <div data-id="element-591">
            {filteredModels.length === 0 ? <div className="py-16 text-center" data-id="element-592">
                <p className="text-gate-text-muted" data-id="element-593">
                  No models match your filters.
                </p>
              </div> : filteredModels.map(model => <ModelCard key={model.id} model={model} data-id="element-594" />)}
          </div>
        </main>
      </div>
    </div>;
}